/**
 * Upload gate for the manual ingestion path (APP-532).
 *
 * In `libs/shared` because the UI enforces the same ceiling client-side before
 * the user hits submit. A copy in the component would drift: raise the API cap
 * and the UI keeps rejecting files the server would have taken; lower it and the
 * UI happily starts an upload the server will refuse.
 */

/**
 * Largest multipart upload the API will buffer.
 *
 * Multer uses memory storage, so this is held in an API-process Buffer for the
 * life of the request — which is why it is 256 MB and not the 3 GB the
 * csvIngestion Lambda can now carry. Gzip is what closes the gap: the 2.09 GB
 * Target daily-store feed is 119 MB compressed, so it fits here with headroom
 * while an uncompressed copy never would. Kept below the Lambda's own
 * MAX_SOURCE_OBJECT_SIZE (512 MB) so the API never accepts a file the processor
 * would then reject.
 */
export const MAX_UPLOAD_BYTES = 256 * 1024 * 1024;

/** Same number in MB, for the UI's size hint and its client-side pre-check. */
export const MAX_UPLOAD_MB = MAX_UPLOAD_BYTES / (1024 * 1024);

/**
 * Gzip magic bytes.
 *
 * The single definition of "is this gzipped" for both the validator (which has
 * to inflate before it can sample) and the S3 upload (which labels the object
 * accordingly). Content sniffing, never the file name — senders mislabel.
 */
export function isGzip(content: Buffer): boolean {
  return content.length >= 2 && content[0] === 0x1f && content[1] === 0x8b;
}

const CSV_MIME_TYPES = ['text/csv', 'application/csv', 'application/vnd.ms-excel', 'text/plain'];

/**
 * Gzip media types. Browsers are inconsistent here: Chrome sends
 * `application/gzip` for `.gz`, Safari and Windows have historically sent
 * `application/x-gzip`, and some send nothing useful at all — hence the
 * extension fallback below.
 */
const GZIP_MIME_TYPES = ['application/gzip', 'application/x-gzip', 'application/x-gzip-compressed', 'gzip'];

/**
 * True when the upload is a CSV or a gzipped CSV.
 *
 * `application/octet-stream` is accepted **only** with a `.gz` extension: it is
 * the generic type a browser falls back to when it cannot classify the file, so
 * accepting it unconditionally would make this filter meaningless. The real
 * content check is still downstream — the validator sniffs gzip magic bytes and
 * csv-parse rejects anything unparseable before a byte reaches S3 — so this is a
 * cheap early rejection, not a security boundary.
 */
export function isAcceptedUpload(mimeType: string, originalName: string): boolean {
  const name = (originalName || '').toLowerCase();
  const mime = (mimeType || '').toLowerCase();

  if (CSV_MIME_TYPES.includes(mime) || GZIP_MIME_TYPES.includes(mime)) {
    return true;
  }
  return mime === 'application/octet-stream' && name.endsWith('.gz');
}

/**
 * Largest object the presigned browser → S3 path will sign (APP-537).
 *
 * The bytes never pass through the API on that path, so the 256 MB multer
 * buffer above is not the constraint. What is: the csvIngestion Lambda refuses
 * any source object *larger than* MAX_SOURCE_OBJECT_SIZE (512 MB) at
 * `head_object`, before it reads a byte. This is set equal to that so the API
 * never signs a URL for a file the processor would then reject, and the number
 * is signed into the URL as Content-Length, so S3 itself enforces it.
 *
 * MAX_SOURCE_OBJECT_SIZE and the gzip handling come from connectly #72.
 */
export const MAX_PRESIGNED_UPLOAD_BYTES = 512 * 1024 * 1024;

/** Same number in MB, for the UI's size hint and its client-side pre-check. */
export const MAX_PRESIGNED_UPLOAD_MB = MAX_PRESIGNED_UPLOAD_BYTES / (1024 * 1024);

/**
 * Above this size the UI presigns instead of POSTing multipart (APP-537).
 *
 * The multipart path validates synchronously and returns precise errors before
 * anything reaches S3; the presigned path cannot, so a bad file fails later as
 * a FAILED history row. So this sits as high as the multipart path can carry,
 * and API Gateway sets that, not multer. The API is an API Gateway REST API in
 * front of the ALB (its responses carry `x-amz-apigw-id`), and a REST API's
 * payload quota is 10 MB and cannot be increased
 * (https://docs.aws.amazon.com/apigateway/latest/developerguide/api-gateway-execution-service-limits-table.html).
 * A larger body gets a 413 with no CORS headers, which the browser reports as a
 * CORS failure — the File Drop bug fixed in #4779.
 *
 * 9 MiB, not 10: the quota counts the whole multipart body, boundaries and part
 * headers included, and AWS says "10 MB" without saying whether that means
 * 10,000,000 or 10,485,760 bytes. 9 MiB clears both, with room for the framing.
 */
export const PRESIGN_THRESHOLD_BYTES = 9 * 1024 * 1024;

/** Same number in MB, for the UI copy. */
export const PRESIGN_THRESHOLD_MB = PRESIGN_THRESHOLD_BYTES / (1024 * 1024);
